import { createContext, useContext } from 'react'

import { JourneyOutletTarget, JourneyTarget } from '@cash-web/shared-journey-util-main'

import { useTargetHistory } from './getTargetHistory'
import { JourneyPaneRendererProps } from './JourneyPaneRenderer'
import { JourneyTargetRenderer } from './JourneyTargetRenderer'

type JourneyOutletOptions = {
  onClose?: () => void
  target: `outlet:${JourneyOutletTarget}`
}

const throwingProxy = new Proxy<never>({} as never, {
  get: (_target, prop) => {
    throw new Error(`Tried to access "${String(prop)}" outside of react context`)
  },
})

export const JourneyOutletContext = createContext<JourneyPaneRendererProps>(throwingProxy)

export const useOutletTargetHistory = (target: JourneyTarget) => {
  const targetRendererProps = useContext(JourneyOutletContext)
  const targetHistories = useTargetHistory(targetRendererProps.history)
  return targetHistories[target as keyof typeof targetHistories] ?? []
}

export const useJourneyOutlet = ({ target, ...props }: JourneyOutletOptions) => {
  const targetHistory = useOutletTargetHistory(target)
  const outlet = targetHistory.length > 0 ? <JourneyOutlet target={target} {...props} /> : null
  return outlet
}

export const JourneyOutlet = ({ target, onClose, ...props }: JourneyOutletOptions) => {
  const targetRendererProps = useContext(JourneyOutletContext)

  return (
    <JourneyTargetRenderer
      {...targetRendererProps}
      {...props}
      target={target}
      onClose={() => {
        targetRendererProps.onClose?.()
        onClose?.()
      }}
    />
  )
}
